import { useApiClient } from '../../hooks/useApiClient';
import { ApiResponse } from '../../types/ApiResponse.type';
import { Case } from '../../types/Case/Case.type';
import { CaseCreate } from '../../types/Case/CaseCreate.type';
import { CaseUpdate } from '../../types/Case/CaseUpdate.type';
import { Disparity } from '../../types/Disparity/Disparity.type';
import { NewDisparity } from '../../types/Disparity/NewDisparity.type';
import { Document } from '../../types/ExportData/Document.type';
import {
  DeleteDisparitiesQueryParams,
  ExportCaseQueryParams,
  GetBuilderBoxesQueryParams,
  GetCaseResourcesQueryParams,
  GetDisparitiesQueryParams,
} from '../../types/QueryParams.type';
import { Resource } from '../../types/Resources/Resource.type.ts';
import { BuilderBox } from '../../types/SimilarlySituated/BuilderBox.type';
import { BuilderBoxCreate } from '../../types/SimilarlySituated/BuilderBoxCreate.type';
import { buildQueryString } from '../../utils/buildQueryString';

export const useCaseApi = () => {
  const apiClient = useApiClient();

  return {
    renameCase: (caseId: number, updateData: { name: string }): Promise<Case> => {
      return apiClient(`/cases/${caseId}`, {
        method: 'PATCH',
        body: JSON.stringify(updateData),
      });
    },
    deleteCase: (caseId: number): Promise<void> => {
      return apiClient(`/cases/${caseId}`, {
        method: 'DELETE',
      });
    },
    createCase: (newCaseData: CaseCreate): Promise<Case> => {
      return apiClient('/cases/', {
        method: 'POST',
        body: JSON.stringify(newCaseData),
      });
    },
    getCaseById: (caseId: number): Promise<Case> => {
      return apiClient(`/cases/${caseId}`);
    },
    getDisparitiesByCaseId: (caseId: number, params: GetDisparitiesQueryParams): Promise<ApiResponse<Disparity[]>> => {
      const queryString = buildQueryString(params);

      return apiClient(`/cases/${caseId}/disparities${queryString}`);
    },

    postDisparityForCase: (caseId: number, disparityData: NewDisparity): Promise<Disparity> => {
      return apiClient(`/cases/${caseId}/disparities`, {
        method: 'POST',
        body: JSON.stringify(disparityData),
      });
    },

    deleteDisparities: async (caseId: number, params?: DeleteDisparitiesQueryParams): Promise<void> => {
      const queryString = buildQueryString(params || {});

      return apiClient(`/cases/${caseId}/disparities${queryString}`, {
        method: 'DELETE',
      });
    },

    exportCase: (caseId: number, params?: ExportCaseQueryParams): Promise<Array<Document>> => {
      const queryString = buildQueryString(params || {});

      return apiClient(`/cases/${caseId}/export${queryString}`);
    },

    updateCase: (caseId: number, updateData: Partial<CaseUpdate>): Promise<Case> => {
      return apiClient(`/cases/${caseId}`, {
        method: 'PATCH',
        body: JSON.stringify(updateData),
      });
    },

    recalculateDisparities: (caseId: number): Promise<void> => {
      return apiClient(`/cases/${caseId}/recalculate_disparities`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },

    getCaseResources: (caseId: number, params: GetCaseResourcesQueryParams): Promise<ApiResponse<Resource[]>> => {
      const queryString = buildQueryString(params);

      return apiClient(`/cases/${caseId}/resources${queryString}`);
    },

    getCaseResourceById: (caseId: number, resourceId: number): Promise<Resource> => {
      return apiClient(`/cases/${caseId}/resources/${resourceId}`);
    },

    applyDisparityCalculation: (caseId: number, disparityData: NewDisparity): Promise<null> => {
      return apiClient(`/cases/${caseId}/disparities/apply_calculation`, {
        method: 'POST',
        body: JSON.stringify(disparityData),
      });
    },

    getBuilderBoxesByCaseId: (
      caseId: number,
      params: GetBuilderBoxesQueryParams,
    ): Promise<ApiResponse<BuilderBox[]>> => {
      const queryString = buildQueryString(params);

      return apiClient(`/cases/${caseId}/builder_boxes${queryString}`);
    },

    createBuilderBox: (caseId: number, builderBox: BuilderBoxCreate): Promise<BuilderBox> => {
      return apiClient(`/cases/${caseId}/builder_boxes`, {
        method: 'POST',
        body: JSON.stringify(builderBox),
      });
    },

    updateCaseResource: (caseId: number, resourceId: number, updateData: Partial<Resource>): Promise<Resource> => {
      return apiClient(`/cases/${caseId}/resources/${resourceId}`, {
        method: 'PATCH',
        body: JSON.stringify(updateData),
      });
    },
  };
};
